<script setup lang="ts">
import { VaultAssetInstallationEntityType } from '~/codegen/graphql'
import type { SearchType } from './GoToSearch.vue'
import useSkillNavigationContext from '~/composables/use-skill-navigation-context'

const props = defineProps<{
  label: string
  icon: string
  objectType: SearchType
  showCreateButton?: boolean
}>()

const emit = defineEmits<{
  create: []
}>()

const route = useRoute()
const isPopoverOpen = ref(false)
const { context: skillNavContext } = useSkillNavigationContext()

function closePopover() {
  isPopoverOpen.value = false
}

const currentLocationId = computed(() => {
  const routeName = route.name.toString()
  const params = route.params as Record<string, string>

  if (props.objectType === 'teamspace' && routeName?.startsWith('teamspace-workspaceId')) {
    return params.workspaceId
  }
  if (props.objectType === 'team') {
    if (routeName?.startsWith('teams-teamId')) {
      return params.teamId
    }
    if (skillNavContext.value.entityType === VaultAssetInstallationEntityType.Team && skillNavContext.value.entityId) {
      return skillNavContext.value.entityId
    }
  }
  if (props.objectType === 'repository') {
    if (routeName?.startsWith('repositories-gid')) {
      return params.gid
    }
    if (
      skillNavContext.value.entityType === VaultAssetInstallationEntityType.Repository &&
      skillNavContext.value.entityId
    ) {
      return skillNavContext.value.entityId
    }
  }
  if (props.objectType === 'bot') {
    if (routeName?.startsWith('bots-botSlug')) {
      return params.botSlug
    }
    if (skillNavContext.value.entityType === VaultAssetInstallationEntityType.Bot && skillNavContext.value.entityId) {
      return skillNavContext.value.entityId
    }
  }

  return null
})
</script>

<template>
  <div class="flex flex-col">
    <UPopover v-model:open="isPopoverOpen" :popper="{ placement: 'right-start' }">
      <div class="group/goToItem flex flex-1 items-center">
        <UButton
          color="white-nav"
          variant="ghost"
          class="group/navItem relative mb-px flex flex-1 justify-between px-2"
        >
          <div class="flex flex-1 items-center gap-x-2 truncate">
            <UIcon :name="icon" class="size-4 shrink-0 grow-0 text-gray-500 dark:text-gray-400" />
            <span class="truncate text-left text-nav font-light text-gray-900 dark:text-white">
              {{ label }}
            </span>
          </div>
          <UButton
            v-if="showCreateButton"
            icon="light:plus"
            color="gray"
            variant="ghost"
            size="xs"
            class="opacity-0 group-hover/goToItem:opacity-100"
            @click.stop="emit('create')"
          />
        </UButton>
      </div>

      <template #panel>
        <div class="w-60 bg-gray-50 p-4 dark:bg-gray-950">
          <NavigationGoToSearch
            :object-type="objectType"
            :show-create-button="showCreateButton"
            @click-link="closePopover"
            @create="emit('create')"
          />
        </div>
      </template>
    </UPopover>

    <div v-if="currentLocationId" class="pl-2">
      <NavigationCurrentLocationWorkspace v-if="objectType === 'teamspace'" :workspace-id="currentLocationId" />
      <NavigationCurrentLocationTeam v-if="objectType === 'team'" :team-id="currentLocationId" />
      <NavigationCurrentLocationRepository v-if="objectType === 'repository'" :repository-gid="currentLocationId" />
      <NavigationCurrentLocationBot v-if="objectType === 'bot'" :bot-identifier="currentLocationId" />
    </div>
  </div>
</template>
